/**
 * AIReportBuilderPage — Mission Control natural-language report builder (v2).
 *
 * v2 upgrade vs v1:
 *  • Truly freeform: backend exposes a **semantic layer** (entities ×
 *    dimensions × measures × ops × grains). Claude composes specs;
 *    the frontend renders whatever it returns.
 *  • Per-saved-report **memory**: every turn carries the full
 *    conversation + current spec; saved reports persist their
 *    thread so you can reopen and keep iterating.
 *  • Multi-block reports: a single report may contain N charts.
 *  • Inline **suggestion chips** Claude returns on every turn — one
 *    tap to refine. These are the heart of the voice-first UX.
 *  • Voice mic drives BOTH initial prompt and subsequent refinement
 *    turns, hands-free.
 *  • `clarify` action surfaces as a question card with chip options.
 *
 * Pipeline:
 *   1. User speaks/types → POST /api/mc/reports/turn with
 *      {prompt, current_report?}
 *   2. Backend asks Claude for an ACTION
 *      (create_report | add_block | modify_block | replace_block |
 *       delete_block | clarify) and applies it to the report state.
 *   3. Backend re-executes every block's query_spec against the
 *      semantic layer, returns the executed report.
 *   4. Frontend renders blocks + suggestion chips + clarify card.
 *
 * RBAC: AdminRoute gates the page; backend re-checks via
 * get_user_rbac_context. Voice input via Web Speech API (hidden when
 * unsupported — typically Firefox).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Sparkles, Mic, MicOff, Send, Save, RotateCw, Loader2, Trash2,
  MessageSquare, BookmarkCheck, ChevronRight, ArrowUpRight, ArrowDownRight,
  HelpCircle, Wand2, Volume2, VolumeX, Globe2, Building2,
  Pin, Home, BarChart3, Check, Undo2, Download, Database,
} from 'lucide-react';
import { toast } from 'sonner';

import api from '../../lib/api';
import { ReportBlockTile } from '../../components/admin/ReportBlockTile';
import SchemaDiscoverySheet from '../../components/admin/SchemaDiscoverySheet';
import { PageShell } from '../../components/layout/PageShell';
import { PageHero } from '../../components/layout/PageHero';
import { getLightSectionTheme } from '../../lib/sectionThemes';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import useSpeechRecognition from '../../hooks/useSpeechRecognition';
import useCoachVoice, { suggestVoiceUri } from '../../hooks/useCoachVoice';
import { useAuth } from '../../context/AuthContext';

// ─── Suggestion chip strip ───────────────────────────────────
function SuggestionChips({ suggestions, onPick, disabled }) {
  if (!suggestions?.length) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="ai-report-suggestion-chips">
      {suggestions.map((s, i) => (
        <button
          key={i}
          type="button"
          disabled={disabled}
          onClick={() => onPick(s.prompt || s.label)}
          className="text-xs px-3 py-1.5 rounded-full border border-[#394fd4]/30 bg-[#394fd4]/[0.06] text-[#394fd4] hover:bg-[#394fd4]/[0.12] disabled:opacity-50 transition-colors inline-flex items-center gap-1.5"
          data-testid={`suggestion-chip-${i}`}
        >
          <Wand2 className="h-3 w-3" />
          {s.label || s.prompt}
        </button>
      ))}
    </div>
  );
}

// ─── Pin-to-dashboard popover ────────────────────────────────
const PIN_SURFACES = [
  { id: 'home', label: 'Mission Control home', icon: Home },
  { id: 'lei_dashboard', label: 'LEI Dashboard', icon: BarChart3 },
];

function PinButton({ disabled, isPinned, onPin, testIdPrefix }) {
  // ``isPinned`` is a {surface: true} map. We surface a tiny check
  // mark next to whichever surfaces the user has already pinned to
  // so re-clicking the same option is obviously a no-op (the backend
  // is idempotent, but the visual confirmation matters).
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-slate-200 bg-white text-slate-600 hover:border-[#394fd4] hover:text-[#394fd4] disabled:opacity-50"
        data-testid={`${testIdPrefix}-trigger`}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Pin to a dashboard"
      >
        <Pin className="h-3 w-3" />
        Pin
      </button>
      {open ? (
        <div
          className="absolute right-0 top-full mt-1 w-52 rounded-md border border-slate-200 bg-white shadow-lg z-10 py-1"
          data-testid={`${testIdPrefix}-menu`}
        >
          {PIN_SURFACES.map((s) => {
            const Icon = s.icon;
            const already = isPinned?.[s.id];
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => { setOpen(false); onPin(s.id); }}
                className="w-full text-left px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                data-testid={`${testIdPrefix}-${s.id}`}
              >
                <Icon className="h-3.5 w-3.5 text-slate-400" />
                <span className="flex-1">{s.label}</span>
                {already ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}


// ─── Page ───────────────────────────────────────────────────
export default function AIReportBuilderPage() {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const incomingPrompt = searchParams.get('prompt');

  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState(null);
  const [savedReports, setSavedReports] = useState([]);
  const [starterPrompts, setStarterPrompts] = useState([]);
  // Full catalog entities[] for the Schema Discovery sheet. Loaded
  // alongside starter prompts in the same /catalog call.
  const [catalogEntities, setCatalogEntities] = useState([]);
  const [schemaOpen, setSchemaOpen] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [activeSavedId, setActiveSavedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);  // hands-free auto-send on final
  const [ttsEnabled, setTtsEnabled] = useState(false); // speak responses aloud
  // Super-admins land on the builder in GLOBAL scope by default so
  // "show LEI for each organization" works on the first try. They can
  // narrow to their home tenant via the hero toggle. Org-admins are
  // always implicitly scoped to their own org by the backend.
  const { isSuperAdmin } = useAuth();
  const [scopeMode, setScopeMode] = useState(isSuperAdmin ? 'global' : 'org');
  // ``pinnedMap`` = {<block_id|'__report__'>: {surface: true}}. Used
  // to show which surfaces each block / the whole report is already
  // pinned to, so re-clicking is a clear no-op.
  const [pinnedMap, setPinnedMap] = useState({});
  const inputRef = useRef(null);
  const lastSpokenIdRef = useRef(null);  // de-dupe TTS across re-renders

  const { supported: voiceSupported, listening, start: startMic, stop: stopMic } =
    useSpeechRecognition();
  const { supported: ttsSupported, voices, speak, cancel: cancelTts, speaking } =
    useCoachVoice();

  const heroTheme = getLightSectionTheme('admin');

  // Saved reports list -----------------------------------------------------
  // Build a query-string suffix that all API calls share. Super-admins
  // can opt into a global cross-tenant view via ``?scope=global``;
  // otherwise the backend honours their current tenant context.
  const scopeQS = useMemo(
    () => (isSuperAdmin && scopeMode === 'global' ? '?scope=global' : ''),
    [isSuperAdmin, scopeMode],
  );

  const fetchList = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get(`/mc/reports/list${scopeQS}`);
      setSavedReports(data?.reports || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load saved reports');
    } finally {
      setLoadingList(false);
    }
  }, [scopeQS]);

  const fetchCatalog = useCallback(async () => {
    try {
      const { data } = await api.get('/mc/reports/catalog');
      setStarterPrompts(data?.starter_prompts || []);
      setCatalogEntities(data?.entities || []);
    } catch {
      // Quietly ignore — catalog is decorative.
    }
  }, []);

  useEffect(() => { fetchList(); fetchCatalog(); }, [fetchList, fetchCatalog]);

  // When scope changes, re-fetch the saved-reports list AND, if a
  // report is open, re-execute it under the new scope so the user
  // sees the right numbers. We intentionally do NOT cancel a pending
  // turn — surprise behavior we'd regret.
  useEffect(() => {
    if (activeSavedId) {
      loadSaved(activeSavedId);
    } else if (report && !generating) {
      // Live draft — clearing is safer than re-executing because the
      // draft has no canonical id we can refetch.
      setReport(null);
      setPrompt('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeQS]);

  // Load a saved report when deep-linked -----------------------------------
  const loadSaved = useCallback(async (id) => {
    setActiveSavedId(id);
    setGenerating(true);
    try {
      const { data } = await api.get(`/mc/reports/${id}${scopeQS}`);
      setReport({ ...data, _saved_id: id });
      setPrompt('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open report');
    } finally {
      setGenerating(false);
    }
  }, [scopeQS]);

  useEffect(() => {
    // Hydrate on initial mount or when the :id param changes. We
    // explicitly compare against the current report's saved id (not
    // ``activeSavedId``) so the very first mount of a deep-linked URL
    // also triggers the load.
    if (reportId) {
      if (report?._saved_id !== reportId) loadSaved(reportId);
    } else if (report?._saved_id) {
      // User navigated from /:id back to the base route — clear state.
      setReport(null);
      setActiveSavedId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  // Run a turn -------------------------------------------------------------
  const runTurn = useCallback(async (text) => {
    const t = (text || '').trim();
    if (!t) {
      toast.error('Type or speak a request first');
      return;
    }
    setGenerating(true);
    try {
      let data;
      if (report?._saved_id) {
        const res = await api.post(`/mc/reports/${report._saved_id}/turn${scopeQS}`, { prompt: t });
        data = res.data;
      } else {
        // Pass our local report state so Claude has memory before save.
        const current = report ? { ...report } : null;
        delete (current || {}).scope;
        const res = await api.post(`/mc/reports/turn${scopeQS}`, {
          prompt: t,
          current_report: current,
        });
        data = res.data;
      }
      // Preserve _saved_id across refines.
      if (report?._saved_id) data._saved_id = report._saved_id;
      setReport(data);
      setPrompt('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Report generation failed');
    } finally {
      setGenerating(false);
    }
  }, [report, scopeQS]);

  const onSubmit = (e) => {
    e?.preventDefault?.();
    if (generating) return;
    runTurn(prompt);
  };

  // Voice handling ---------------------------------------------------------
  // Pick a sensible default voice. Re-runs only when the voice list
  // hydrates or the user identity changes.
  const ttsVoiceUri = useMemo(
    () => suggestVoiceUri(voices, { gender: 'neutral', lang: 'en-US' }),
    [voices],
  );

  // Pause mic capture while we're speaking — otherwise the synthesized
  // voice gets picked up by the recogniser as if the user spoke it,
  // creating an instant feedback loop.
  useEffect(() => {
    if (speaking && listening) stopMic();
  }, [speaking, listening, stopMic]);

  // Speak the latest assistant turn whenever TTS is enabled and a
  // new report state lands. We de-dupe on a (turn-count × report-id)
  // signature so refreshes / re-renders don't re-speak.
  useEffect(() => {
    if (!ttsEnabled || !ttsSupported || !report) return;
    const turns = report.conversation?.length || 0;
    const sig = `${report._saved_id || 'draft'}::${turns}`;
    if (lastSpokenIdRef.current === sig) return;
    lastSpokenIdRef.current = sig;
    // Compose what to read: clarify question takes precedence, then
    // the report's narrative, then the headline + suggestion hint.
    let textToSpeak = '';
    if (report.last_action === 'clarify' && report.clarify_question) {
      textToSpeak = report.clarify_question;
    } else if (report.narrative) {
      textToSpeak = report.narrative;
    } else if (report.headline) {
      textToSpeak = report.headline;
    }
    if (textToSpeak && (report.suggestions || []).length) {
      const next = (report.suggestions || [])
        .slice(0, 3)
        .map((s) => s.label)
        .filter(Boolean);
      if (next.length) {
        textToSpeak += ` You could also: ${next.join('; ')}.`;
      }
    }
    if (textToSpeak.trim()) {
      speak(textToSpeak.trim(), { voiceUri: ttsVoiceUri, rate: 1.05 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, ttsEnabled, ttsSupported]);

  // In "voice mode" (hands-free), STT auto-sends ONLY after the
  // recogniser has been silent long enough that we're confident the
  // user finished their thought. The hook tracks accumulated finals
  // across pauses so a mid-sentence breath no longer wipes the
  // prompt and restarts on a new line — the previous behavior the
  // user complained about. ``silenceEnd: true`` arrives ~2.5s after
  // the last bit of speech (see useSpeechRecognition `silenceMs`).
  const onMicResult = useCallback((text, info) => {
    setPrompt(text);
    if (voiceMode && info?.silenceEnd && text.trim().length > 2) {
      stopMic();
      runTurn(text);
    }
  }, [voiceMode, stopMic, runTurn]);

  const onMicClick = () => {
    if (!voiceSupported) {
      toast.error('Voice input unsupported in this browser');
      return;
    }
    if (listening) { stopMic(); return; }
    startMic(onMicResult);
  };

  const toggleVoiceMode = () => {
    setVoiceMode((v) => {
      const next = !v;
      if (!next && listening) stopMic();
      return next;
    });
  };

  // Save / delete ----------------------------------------------------------
  const onSave = async () => {
    if (!report) return;
    setSaving(true);
    try {
      const payload = { report: { ...report }, name: report?.title };
      delete payload.report.scope;
      delete payload.report._saved_id;
      const { data } = await api.post(`/mc/reports/save${scopeQS}`, payload);
      toast.success('Saved to workspace');
      setSavedReports((rs) => [data, ...rs]);
      setReport((r) => ({ ...r, _saved_id: data.id }));
      setActiveSavedId(data.id);
      navigate(`/admin/reports/builder/${data.id}`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Remove this saved report?')) return;
    try {
      await api.delete(`/mc/reports/${id}${scopeQS}`);
      setSavedReports((rs) => rs.filter((r) => r.id !== id));
      if (activeSavedId === id) {
        setActiveSavedId(null);
        setReport(null);
        navigate('/admin/reports/builder', { replace: true });
      }
      toast.success('Removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const onReset = () => {
    setReport(null);
    setPrompt('');
    setActiveSavedId(null);
    navigate('/admin/reports/builder', { replace: true });
    inputRef.current?.focus();
  };

  // Undo handler ----------------------------------------------------------
  // We do an OPTIMISTIC client-side restore first (instant feedback)
  // then sync to the server. For saved reports the server is the source
  // of truth so we always confirm via the response. For drafts the
  // server-side undo also re-executes blocks against live data, which
  // is important because the cached row data on the client may be stale.
  const undoable = useMemo(() => {
    const convo = report?.conversation || [];
    return convo.some((e) => e.role === 'assistant' && e.pre_state);
  }, [report]);

  const onUndo = useCallback(async () => {
    if (!undoable || generating) return;
    // Cancel any in-flight TTS — speaking the OLD narrative while
    // we're showing the prior chart would be jarring.
    cancelTts();
    setGenerating(true);
    try {
      let data;
      if (report?._saved_id) {
        const res = await api.post(`/mc/reports/${report._saved_id}/undo`);
        data = res.data;
        data._saved_id = report._saved_id;
      } else {
        const current = { ...report };
        delete current.scope;
        const res = await api.post('/mc/reports/undo', { current_report: current });
        data = res.data;
      }
      setReport(data);
      toast.success('Undid last turn');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not undo');
    } finally {
      setGenerating(false);
    }
  }, [report, undoable, generating, cancelTts]);

  // Export handler --------------------------------------------------------
  // Streams a zip from the backend (report.md + per-block CSVs) and
  // triggers a browser download. Available only on SAVED reports —
  // we need a stable id; if the user is on a draft we silently save
  // first then export, mirroring the pin-from-draft flow.
  const [exporting, setExporting] = useState(false);
  const onExport = useCallback(async () => {
    if (!report || exporting) return;
    let savedId = report?._saved_id;
    setExporting(true);
    try {
      if (!savedId) {
        const payload = { report: { ...report }, name: report?.title };
        delete payload.report.scope;
        delete payload.report._saved_id;
        const { data: saved } = await api.post(`/mc/reports/save${scopeQS}`, payload);
        savedId = saved.id;
        setSavedReports((rs) => [saved, ...rs]);
        setReport((r) => ({ ...r, _saved_id: savedId }));
        setActiveSavedId(savedId);
        navigate(`/admin/reports/builder/${savedId}`, { replace: true });
      }
      const res = await api.get(`/mc/reports/${savedId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      // Try to read filename from Content-Disposition, fall back to report title.
      const cd = res.headers?.['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `${(report?.title || 'report').replace(/\s+/g, '_')}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report bundle downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [report, exporting, scopeQS, navigate]);

  // Pin handler -----------------------------------------------------------
  // Pinning is only meaningful AFTER a draft has been saved (the
  // pin holds a foreign-key to the report id). For unsaved drafts we
  // silently save first, then pin in one user-visible click.
  const onPin = useCallback(async (surface, blockId = null) => {
    let savedId = report?._saved_id;
    if (!savedId) {
      try {
        setSaving(true);
        const payload = { report: { ...report }, name: report?.title };
        delete payload.report.scope;
        delete payload.report._saved_id;
        const { data } = await api.post(`/mc/reports/save${scopeQS}`, payload);
        savedId = data.id;
        setSavedReports((rs) => [data, ...rs]);
        setReport((r) => ({ ...r, _saved_id: savedId }));
        setActiveSavedId(savedId);
        navigate(`/admin/reports/builder/${savedId}`, { replace: true });
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not save before pinning');
        return;
      } finally {
        setSaving(false);
      }
    }
    try {
      await api.post(`/mc/reports/${savedId}/pin`, { surface, block_id: blockId });
      const targetKey = blockId || '__report__';
      setPinnedMap((m) => ({ ...m, [targetKey]: { ...(m[targetKey] || {}), [surface]: true } }));
      const niceSurface = surface === 'home' ? 'Mission Control home' : 'LEI Dashboard';
      toast.success(`Pinned to ${niceSurface}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not pin');
    }
  }, [report, scopeQS, navigate]);

  // If we arrived from a pinned-tile annotation click OR from a
  // glossary "Show me an example chart" button (carrying a
  // ``?prompt=…`` query param), auto-fire that turn. One-shot —
  // strip the param after so a hard refresh doesn't keep
  // re-running it. Fires whether or not a report already exists:
  // an empty builder seeds a brand-new report; an open report
  // refines into it.
  useEffect(() => {
    if (!incomingPrompt || generating) return;
    const t = incomingPrompt;
    setSearchParams((p) => { const np = new URLSearchParams(p); np.delete('prompt'); return np; }, { replace: true });
    runTurn(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingPrompt]);

  const onPickSuggestion = (text) => {
    setPrompt(text);
    runTurn(text);
  };

  const starters = useMemo(() => starterPrompts.slice(0, 6), [starterPrompts]);

  return (
    <PageShell
      theme={heroTheme}
      hero={(
        <PageHero
          title={(
            <>
              <span className="text-[#394fd4]">AI Report Builder</span>
              <span className="text-slate-900"> — Ask, refine, save</span>
            </>
          )}
          description="Type or speak in plain English. Claude composes from a semantic layer — entities, dimensions, measures — and remembers every turn so you can iterate by saying things like “add a trend line” or “group by manager too.”"
          theme={heroTheme}
          testId="ai-report-builder-hero"
          actions={(
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSchemaOpen(true)}
                disabled={generating}
                className="gap-1.5"
                data-testid="ai-report-schema-btn"
                title="Browse every entity + measure available to ask about"
              >
                <Database className="h-3.5 w-3.5" />
                Schema
              </Button>
              {isSuperAdmin ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setScopeMode((m) => (m === 'global' ? 'org' : 'global'))}
                  disabled={generating}
                  className="gap-1.5"
                  data-testid="ai-report-scope-toggle-btn"
                  title={scopeMode === 'global'
                    ? 'Currently viewing every tenant — click to scope to your home org'
                    : 'Currently scoped to your home org — click to expand to global cross-tenant view'}
                >
                  {scopeMode === 'global'
                    ? <Globe2 className="h-3.5 w-3.5" />
                    : <Building2 className="h-3.5 w-3.5" />}
                  {scopeMode === 'global' ? 'Global view' : 'This org'}
                </Button>
              ) : null}
              {ttsSupported ? (
                <Button
                  variant={ttsEnabled ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setTtsEnabled((v) => {
                      const next = !v;
                      if (!next) cancelTts();
                      return next;
                    });
                  }}
                  className={ttsEnabled
                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5'
                    : 'gap-1.5'}
                  data-testid="ai-report-tts-btn"
                  title={ttsEnabled
                    ? 'Reports read aloud — click to mute'
                    : 'Click to have reports read aloud'}
                >
                  {ttsEnabled
                    ? (speaking
                      ? <Volume2 className="h-3.5 w-3.5 animate-pulse" />
                      : <Volume2 className="h-3.5 w-3.5" />)
                    : <VolumeX className="h-3.5 w-3.5" />}
                  {ttsEnabled ? (speaking ? 'Speaking…' : 'Read aloud on') : 'Read aloud off'}
                </Button>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                onClick={onUndo}
                disabled={generating || !undoable}
                className="gap-1.5"
                data-testid="ai-report-undo-btn"
                title={undoable ? 'Pop the last turn and restore the prior chart' : 'No turn to undo yet'}
              >
                <Undo2 className="h-3.5 w-3.5" />
                Undo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onReset}
                disabled={generating}
                className="gap-1.5"
                data-testid="ai-report-reset-btn"
              >
                <RotateCw className="h-3.5 w-3.5" />
                New report
              </Button>
              {report ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onExport}
                  disabled={exporting || generating}
                  className="gap-1.5"
                  data-testid="ai-report-export-btn"
                  title="Download report.md + per-block CSVs as a zip"
                >
                  {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                  Export
                </Button>
              ) : null}
              {report && !report._saved_id ? (
                <Button
                  size="sm"
                  onClick={onSave}
                  disabled={saving}
                  className="gap-1.5 bg-[#394fd4] hover:bg-[#2f43b8] text-white"
                  data-testid="ai-report-save-btn"
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  Save to workspace
                </Button>
              ) : null}
            </div>
          )}
        />
      )}
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6"
        data-testid="ai-report-builder-page">
        {/* ── Main canvas ──────────────────────────────────── */}
        <div className="space-y-5 min-w-0">
          {/* Prompt bar */}
          <form onSubmit={onSubmit}
            className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-2"
            data-testid="ai-report-prompt-form">
            <Sparkles className="h-4 w-4 text-[#394fd4] flex-shrink-0 ml-1" />
            <Input
              ref={inputRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={report
                ? 'Refine… "add a trend line" · "group by manager" · "switch to line chart"'
                : 'Ask for any report… "completion rate by department, with a target line at 80%"'}
              disabled={generating}
              className="border-0 focus-visible:ring-0 shadow-none text-sm"
              data-testid="ai-report-prompt-input"
            />
            {voiceSupported ? (
              <>
                <Button
                  type="button"
                  variant={voiceMode ? 'default' : 'outline'}
                  size="sm"
                  onClick={toggleVoiceMode}
                  disabled={generating}
                  className={voiceMode ? 'bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5' : 'gap-1.5'}
                  data-testid="ai-report-voice-mode-btn"
                  title="Hands-free mode: auto-sends when you stop speaking"
                >
                  <Mic className="h-3.5 w-3.5" />
                  {voiceMode ? 'Hands-free' : 'Hands-free off'}
                </Button>
                <Button
                  type="button"
                  variant={listening ? 'default' : 'outline'}
                  size="icon"
                  onClick={onMicClick}
                  disabled={generating}
                  className={listening ? 'bg-rose-600 hover:bg-rose-700 text-white' : ''}
                  data-testid="ai-report-mic-btn"
                  aria-label={listening ? 'Stop listening' : 'Start voice input'}
                >
                  {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </Button>
              </>
            ) : null}
            <Button
              type="submit"
              size="icon"
              disabled={generating || !prompt.trim()}
              className="bg-[#394fd4] hover:bg-[#2f43b8] text-white"
              data-testid="ai-report-submit-btn"
              aria-label="Generate"
            >
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>

          {/* Starter chips (only when no report yet) */}
          {!report && starters.length ? (
            <div className="flex flex-wrap gap-2" data-testid="ai-report-starter-chips">
              {starters.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => { setPrompt(p); inputRef.current?.focus(); }}
                  className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-[#394fd4] hover:text-[#394fd4] transition-colors"
                  data-testid={`ai-report-starter-${p.slice(0, 12).replace(/\s/g, '-').toLowerCase()}`}
                >
                  {p}
                </button>
              ))}
              {/* "Browse schema" chip — pairs with the static starter
                  prompts so users always have a way to discover what
                  to ask without trial-and-error. */}
              <button
                type="button"
                onClick={() => setSchemaOpen(true)}
                className="text-xs px-3 py-1.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors inline-flex items-center gap-1.5"
                data-testid="ai-report-browse-schema-chip"
              >
                <Database className="h-3 w-3" />
                Browse what I can ask…
              </button>
            </div>
          ) : null}

          {/* Empty state */}
          {!report && !generating ? (
            <Card className="border-dashed border-slate-300 bg-slate-50/50">
              <CardContent className="py-12 text-center">
                <Sparkles className="h-8 w-8 mx-auto mb-3 text-slate-400" />
                <div className="text-sm text-slate-600 font-medium">
                  Type or pick a starter prompt above
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  Claude composes from a semantic layer — try “new signups per week as a line chart”, then ask for a trend line or a target.
                </div>
              </CardContent>
            </Card>
          ) : null}

          {/* Generating spinner */}
          {generating && !report ? (
            <div className="rounded-xl border border-slate-200 bg-white p-8 flex items-center justify-center gap-3 text-slate-600 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Building your report…
            </div>
          ) : null}

          {/* Clarify question */}
          {report?.last_action === 'clarify' && report?.clarify_question ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4"
              data-testid="ai-report-clarify">
              <div className="flex items-start gap-2">
                <HelpCircle className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-amber-900 mb-1">
                    Claude needs a bit more
                  </div>
                  <div className="text-sm text-amber-800 mb-3">
                    {report.clarify_question}
                  </div>
                  <SuggestionChips
                    suggestions={report.clarify_options || []}
                    onPick={onPickSuggestion}
                    disabled={generating}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {/* Report */}
          {report && (report.blocks?.length || 0) > 0 ? (
            <div className="space-y-5" data-testid="ai-report-draft">
              <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-[#394fd4]/[0.04] to-white p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs uppercase tracking-wider text-[#394fd4] font-semibold mb-1">
                      Report
                    </div>
                    <div className="text-xl font-semibold text-slate-900 mb-1"
                      data-testid="ai-report-title">
                      {report.title || 'Untitled'}
                    </div>
                    {report.headline ? (
                      <div className="text-sm text-slate-700 mb-3"
                        data-testid="ai-report-headline">
                        {report.headline}
                      </div>
                    ) : null}
                    {report.narrative ? (
                      <div className="text-sm text-slate-600 leading-relaxed"
                        data-testid="ai-report-narrative">
                        {report.narrative}
                      </div>
                    ) : null}
                  </div>
                  <PinButton
                    disabled={generating || saving}
                    isPinned={pinnedMap.__report__}
                    onPin={(surface) => onPin(surface, null)}
                    testIdPrefix="ai-report-pin"
                  />
                </div>
              </div>

              {(report.blocks || []).map((b) => (
                <div key={b.id} className="space-y-2" data-testid={`report-block-${b.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-slate-700">
                      {b?.chart_spec?.y_label || b?.chart_spec?.x_label || b?.id}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200 capitalize">
                        {b?.chart_spec?.chart_type || 'block'}
                      </Badge>
                      <PinButton
                        disabled={generating || saving}
                        isPinned={pinnedMap[b.id]}
                        onPin={(surface) => onPin(surface, b.id)}
                        testIdPrefix={`block-pin-${b.id}`}
                      />
                    </div>
                  </div>
                  <ReportBlockTile
                    block={b}
                    onAnnotationClick={onPickSuggestion}
                    onRefinePrompt={(text) => {
                      // Plan-editor click on the "What did Claude
                      // pick?" disclosure — drop the suggested refine
                      // prompt into the input box (not auto-submit),
                      // so the admin can tweak the wording before
                      // sending. Focus the input on the next tick so
                      // the cursor lands at the end.
                      if (!text) return;
                      setPrompt(text);
                      setTimeout(() => inputRef.current?.focus(), 30);
                    }}
                  />
                </div>
              ))}

              {/* Refinement suggestions — the heart of the voice-first loop */}
              {(() => {
                // Augment Claude's suggestions with a static "+ filter
                // on a value" chip. It advertises the HAVING capability
                // to admins who don't yet know they can ask for
                // value-thresholded reports ("only orgs above 60%",
                // "ignore zero values"). Generated from the first
                // block's measure label so the prompt feels concrete.
                const firstBlock = (report.blocks || [])[0];
                const measureLabel =
                  firstBlock?.chart_spec?.y_fields?.[0]?.label
                  || firstBlock?.chart_spec?.y_label
                  || 'this value';
                const valueChip = {
                  label: `+ filter on ${String(measureLabel).toLowerCase()}`,
                  prompt: `Only show rows where ${measureLabel} is above 60`,
                };
                const baseSuggestions = report.suggestions || [];
                const augmented = [...baseSuggestions, valueChip];
                if (!augmented.length) return null;
                return (
                  <div className="space-y-2">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                      Try next
                    </div>
                    <SuggestionChips
                      suggestions={augmented}
                      onPick={onPickSuggestion}
                      disabled={generating}
                    />
                  </div>
                );
              })()}

              {report._saved_id ? (
                <div className="flex items-center gap-2 text-xs text-slate-500 italic">
                  <BookmarkCheck className="h-3.5 w-3.5 text-emerald-600" />
                  Saved. Every refinement turn updates this saved report.
                </div>
              ) : null}
            </div>
          ) : null}

          {/* Conversation thread */}
          {report?.conversation?.length ? (
            <details className="rounded-lg border border-slate-200 bg-white"
              data-testid="ai-report-conversation">
              <summary className="px-4 py-2.5 text-xs font-medium text-slate-600 cursor-pointer flex items-center gap-2">
                <MessageSquare className="h-3.5 w-3.5" />
                Conversation ({report.conversation.length})
              </summary>
              <div className="border-t border-slate-100 divide-y divide-slate-100">
                {report.conversation.map((h, i) => (
                  <div key={i} className="px-4 py-2 text-xs flex gap-2">
                    <span className={`uppercase tracking-wide font-semibold ${
                      h.role === 'user' ? 'text-[#394fd4]' : 'text-slate-500'
                    }`}>{h.role}</span>
                    <div className="flex-1 text-slate-700">
                      <div>{h.content}</div>
                      {h.action?.kind ? (
                        <div className="text-[10px] text-slate-400 mt-0.5">
                          {h.action.kind}
                          {h.action.target_block_id ? ` · ${h.action.target_block_id}` : ''}
                          {h.action.mod_count ? ` · ${h.action.mod_count} edit${h.action.mod_count > 1 ? 's' : ''}` : ''}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          ) : null}
        </div>

        {/* ── Workspace ──────────────────────────────────────── */}
        <aside className="space-y-3 min-w-0" data-testid="ai-report-workspace">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 flex items-center gap-2">
            <BookmarkCheck className="h-3.5 w-3.5 text-[#394fd4]" />
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
              Saved reports
            </span>
            <span className="text-[11px] text-slate-500 ml-auto">{savedReports.length}</span>
          </div>
          <div className="space-y-2">
            {loadingList ? (
              <div className="text-xs text-slate-500 italic px-1">Loading…</div>
            ) : savedReports.length === 0 ? (
              <div className="text-xs text-slate-500 italic px-1">
                No saved reports yet. Generate one and click "Save to workspace".
              </div>
            ) : (
              savedReports.map((r) => (
                <div
                  key={r.id}
                  className={`rounded-lg border p-3 bg-white hover:border-[#394fd4] transition-colors ${
                    activeSavedId === r.id ? 'border-[#394fd4]' : 'border-slate-200'
                  }`}
                  data-testid={`saved-report-${r.id}`}
                >
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/reports/builder/${r.id}`)}
                    className="w-full text-left"
                  >
                    <div className="text-sm font-medium text-slate-800 line-clamp-2 flex items-start gap-1">
                      <ChevronRight className="h-3.5 w-3.5 mt-0.5 text-slate-400 flex-shrink-0" />
                      <span>{r.name}</span>
                    </div>
                    {r.headline ? (
                      <div className="text-[11px] text-slate-500 mt-1 line-clamp-2 pl-4">
                        {r.headline}
                      </div>
                    ) : null}
                    <div className="text-[10px] text-slate-400 mt-1.5 pl-4 inline-flex items-center gap-2">
                      <span>{new Date(r.created_at).toLocaleDateString()}</span>
                      {r.blocks ? <span>· {r.blocks.length} block{r.blocks.length > 1 ? 's' : ''}</span> : null}
                      {r.conversation ? <span>· {r.conversation.length} turn{r.conversation.length > 1 ? 's' : ''}</span> : null}
                    </div>
                  </button>
                  <div className="flex justify-end pt-2">
                    <button
                      type="button"
                      onClick={() => onDelete(r.id)}
                      className="text-[10px] text-rose-600 hover:text-rose-700 inline-flex items-center gap-1"
                      data-testid={`saved-report-delete-${r.id}`}
                    >
                      <Trash2 className="h-3 w-3" /> Remove
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </aside>
      </div>
      {/* Slide-over schema discovery panel. Mounted at the PageShell
          root so it's not clipped by the builder's grid container. */}
      <SchemaDiscoverySheet
        open={schemaOpen}
        onOpenChange={setSchemaOpen}
        entities={catalogEntities}
        onPickPrompt={(p) => {
          setPrompt(p);
          // Focus the input on the next tick so the cursor lands in
          // the right place after the sheet's close animation.
          setTimeout(() => inputRef.current?.focus(), 80);
        }}
      />
    </PageShell>
  );
}
